import { deviceWithID, fetchDevices, getAllDevices } from "@shared/deviceFeed.js";
import { colorWithID, fetchColors, getAllColors, getColoredOverlayImageID } from "@shared/colorFeed.js";
import { stickerSetWithID, fetchStickerSets, allStickerSets } from "@shared/stickerSetFeed.js";
import { CYODesign } from "@shared/CYODesign.js";
import * as stickerSetFeed from "@shared/stickerSetFeed.js";
import { Compositor } from "@shared/Compositor.js";
import { optionPanels } from "./OptionPanels.js";
import { CartUtils } from "@shared/CartUtils.js";
import { Product } from "@shared/Product.js";
import imageStorage from "@shared/ImageStorage.js";
import { cfProcessImage, convertImageToFile, loadImage } from "@shared/imgUtil.js";
import * as imageCache from "@shared/imageCache.js";
import { cfAccountHash } from "@shared/cloudflare.js";
import { getCurrentServerEnv, setServerEnv } from "@shared/environment.js";
import dogStickerUrl from "./images/dog-sticker.png";
import joeImageUrl from "./images/joe.jpg";
import landscapeImageUrl from "./images/landscape.jpg";
import spacePortraitImageUrl from "./images/space-portrait.jpg";
import blueSquareUrl from "./images/blue-square.png";
import colorGridUrl from "./images/color-grid.jpg";
import fruitImageUrl from "./images/fruit.jpg";
import ginghamImageUrl from "./images/gingham.png";

// Default to showing single device

// Local storage keys
const STORAGE_KEY_DEVICE_ID = "device-sticker-test-device-id";
const STORAGE_KEY_STICKER_SET_ID = "device-sticker-test-sticker-set-id";
const STORAGE_KEY_STICKER_PLACEMENT = "device-sticker-test-sticker-placement";
const STORAGE_KEY_USER_IMAGE = "device-sticker-test-user-image";
const STORAGE_KEY_USER_IMAGE_PLACEMENT = "device-sticker-test-user-image-placement";
const STORAGE_KEY_USER_IMAGE_STYLE = "device-sticker-test-user-image-style";
const STORAGE_KEY_DEBUG_BG = "device-sticker-test-debug-bg";
const STORAGE_KEY_DISABLE_ROTATIONS = "device-sticker-test-disable-rotations";
const STORAGE_KEY_DEBUG_SHOW_SAFE_AREA = "device-sticker-test-debug-show-safe-area";
const STORAGE_KEY_DEBUG_SHOW_BLEED_INSETS = "device-sticker-test-debug-show-bleed-insets";
const STORAGE_KEY_DEBUG_HIDE_PRODUCT = "device-sticker-test-debug-hide-product";
const STORAGE_KEY_DEBUG_HIDE_MASK = "device-sticker-test-debug-hide-mask";
const STORAGE_KEY_DEBUG_ISOLATE_MASK = "device-sticker-test-debug-isolate-mask";
const STORAGE_KEY_DEBUG_HIDE_OVERLAY = "device-sticker-test-debug-hide-overlay";
const STORAGE_KEY_DEBUG_HIDE_COLORED_OVERLAY = "device-sticker-test-debug-hide-colored-overlay";
const STORAGE_KEY_SHOW_PRINT_IMAGE = "device-sticker-test-show-print-image";
const STORAGE_KEY_BG_IMAGE = "device-sticker-test-bg-image";

// Flag to control debug backgrounds rendering (now handled by CYODesign.current.debugOptions.drawBgs)

// Mode tracking for parameter panel visibility
let currentMode = "single"; // "single" or "multiple"

// Show print image flag
let showPrintImage = false;

// Mode tracking functions
function setMode(mode) {
    currentMode = mode;
    const parameterPanel = document.getElementById("parameter-panel");
    if (mode === "single") {
        parameterPanel.classList.add("visible");
    } else {
        parameterPanel.classList.remove("visible");
    }
}

// Save settings to localStorage
function saveSettings() {
    try {
        // Save device ID
        const deviceId = document.getElementById("device-id").value;
        if (deviceId) {
            localStorage.setItem(STORAGE_KEY_DEVICE_ID, deviceId);
        }

        // Save sticker set ID
        const stickerSetId = document.getElementById("sticker-set-id").value;
        localStorage.setItem(STORAGE_KEY_STICKER_SET_ID, stickerSetId);

        // Save sticker placement type
        const stickerPlacementType = document.getElementById("placement-type").value;
        if (stickerPlacementType) {
            localStorage.setItem(STORAGE_KEY_STICKER_PLACEMENT, stickerPlacementType);
        }


        // Save user image
        const userImage = document.getElementById("user-image").value;
        localStorage.setItem(STORAGE_KEY_USER_IMAGE, userImage);

        // Save user image placement
        const userImagePlacement = document.getElementById("user-image-placement").value;
        if (userImagePlacement) {
            localStorage.setItem(STORAGE_KEY_USER_IMAGE_PLACEMENT, userImagePlacement);
        }

        // Save user image style
        const userImageStyle = document.getElementById("user-image-style").value;
        if (userImageStyle) {
            localStorage.setItem(STORAGE_KEY_USER_IMAGE_STYLE, userImageStyle);
        }

        // Save debug backgrounds state
        localStorage.setItem(STORAGE_KEY_DEBUG_BG, CYODesign.current.debugOptions.drawBgs.toString());

        // Save disable rotations state
        localStorage.setItem(STORAGE_KEY_DISABLE_ROTATIONS, CYODesign.current.debugOptions.disableRotations.toString());

        // Save debug show safe area state
        localStorage.setItem(STORAGE_KEY_DEBUG_SHOW_SAFE_AREA, CYODesign.current.debugOptions.showSafeArea.toString());

        // Save debug show bleed insets state
        localStorage.setItem(STORAGE_KEY_DEBUG_SHOW_BLEED_INSETS, CYODesign.current.debugOptions.showBleedInsets.toString());

        // Save debug hide product state
        localStorage.setItem(STORAGE_KEY_DEBUG_HIDE_PRODUCT, CYODesign.current.debugOptions.hideProduct.toString());

        // Save debug hide mask state
        localStorage.setItem(STORAGE_KEY_DEBUG_HIDE_MASK, CYODesign.current.debugOptions.hideMask.toString());

        // Save debug isolate mask state
        localStorage.setItem(STORAGE_KEY_DEBUG_ISOLATE_MASK, CYODesign.current.debugOptions.isolateMask.toString());

        // Save debug hide overlay state
        localStorage.setItem(STORAGE_KEY_DEBUG_HIDE_OVERLAY, CYODesign.current.debugOptions.hideOverlay.toString());

        // Save debug hide colored overlay state
        localStorage.setItem(STORAGE_KEY_DEBUG_HIDE_COLORED_OVERLAY, CYODesign.current.debugOptions.hideColoredOverlay.toString());

        // Save show print image state
        localStorage.setItem(STORAGE_KEY_SHOW_PRINT_IMAGE, showPrintImage.toString());

        // Save bg image
        const bgImage = document.getElementById("bg-image").value;
        localStorage.setItem(STORAGE_KEY_BG_IMAGE, bgImage);
    } catch (error) {
        console.error("Error saving settings to localStorage:", error);
    }
}

// Load settings from localStorage
function loadSettings() {
    try {
        // Load debug backgrounds setting
        const debugBgSetting = localStorage.getItem(STORAGE_KEY_DEBUG_BG);
        if (debugBgSetting !== null) {
            CYODesign.current.debugOptions.drawBgs = debugBgSetting === "true";
        }

        // Load disable rotations setting
        const disableRotationsSetting = localStorage.getItem(STORAGE_KEY_DISABLE_ROTATIONS);
        if (disableRotationsSetting !== null) {
            CYODesign.current.debugOptions.disableRotations = disableRotationsSetting === "true";
        }

        // Load debug show safe area setting
        const debugShowSafeAreaSetting = localStorage.getItem(STORAGE_KEY_DEBUG_SHOW_SAFE_AREA);
        if (debugShowSafeAreaSetting !== null) {
            CYODesign.current.debugOptions.showSafeArea = debugShowSafeAreaSetting === "true";
        }

        // Load debug show bleed insets setting
        const debugShowBleedInsetsSetting = localStorage.getItem(STORAGE_KEY_DEBUG_SHOW_BLEED_INSETS);
        if (debugShowBleedInsetsSetting !== null) {
            CYODesign.current.debugOptions.showBleedInsets = debugShowBleedInsetsSetting === "true";
        }

        // Load debug hide product setting
        const debugHideProductSetting = localStorage.getItem(STORAGE_KEY_DEBUG_HIDE_PRODUCT);
        if (debugHideProductSetting !== null) {
            CYODesign.current.debugOptions.hideProduct = debugHideProductSetting === "true";
        }

        // Load debug hide mask setting
        const debugHideMaskSetting = localStorage.getItem(STORAGE_KEY_DEBUG_HIDE_MASK);
        if (debugHideMaskSetting !== null) {
            CYODesign.current.debugOptions.hideMask = debugHideMaskSetting === "true";
        }

        // Load debug isolate mask setting
        const debugIsolateMaskSetting = localStorage.getItem(STORAGE_KEY_DEBUG_ISOLATE_MASK);
        if (debugIsolateMaskSetting !== null) {
            CYODesign.current.debugOptions.isolateMask = debugIsolateMaskSetting === "true";
        }

        // Load debug hide overlay setting
        const debugHideOverlaySetting = localStorage.getItem(STORAGE_KEY_DEBUG_HIDE_OVERLAY);
        if (debugHideOverlaySetting !== null) {
            CYODesign.current.debugOptions.hideOverlay = debugHideOverlaySetting === "true";
        }

        // Load debug hide colored overlay setting
        const debugHideColoredOverlaySetting = localStorage.getItem(STORAGE_KEY_DEBUG_HIDE_COLORED_OVERLAY);
        if (debugHideColoredOverlaySetting !== null) {
            CYODesign.current.debugOptions.hideColoredOverlay = debugHideColoredOverlaySetting === "true";
        }

        // Load show print image setting
        const showPrintImageSetting = localStorage.getItem(STORAGE_KEY_SHOW_PRINT_IMAGE);
        if (showPrintImageSetting !== null) {
            showPrintImage = showPrintImageSetting === "true";
        }

        return {
            deviceId: localStorage.getItem(STORAGE_KEY_DEVICE_ID),
            stickerSetId: localStorage.getItem(STORAGE_KEY_STICKER_SET_ID),
            stickerPlacementType: localStorage.getItem(STORAGE_KEY_STICKER_PLACEMENT),
            userImage: localStorage.getItem(STORAGE_KEY_USER_IMAGE),
            userImagePlacement: localStorage.getItem(STORAGE_KEY_USER_IMAGE_PLACEMENT),
            userImageStyle: localStorage.getItem(STORAGE_KEY_USER_IMAGE_STYLE),
            bgImage: localStorage.getItem(STORAGE_KEY_BG_IMAGE),
            debugBg: CYODesign.current.debugOptions.drawBgs,
            disableRotations: CYODesign.current.debugOptions.disableRotations,
            debugShowSafeArea: CYODesign.current.debugOptions.showSafeArea,
            debugShowBleedInsets: CYODesign.current.debugOptions.showBleedInsets,
            debugHideProduct: CYODesign.current.debugOptions.hideProduct,
            debugHideMask: CYODesign.current.debugOptions.hideMask,
            debugIsolateMask: CYODesign.current.debugOptions.isolateMask,
            debugHideOverlay: CYODesign.current.debugOptions.hideOverlay,
            debugHideColoredOverlay: CYODesign.current.debugOptions.hideColoredOverlay,
            showPrintImage: showPrintImage
        };
    } catch (error) {
        console.error("Error loading settings from localStorage:", error);
        return {};
    }
}

async function initializeTest() {
    try {
        // Set up event listener for Show All Devices button
        document.getElementById("show-all-devices").addEventListener("click", () => {
            clearContainer();
            setMode("multiple");
            showAllDevices();
        });

        // Set up event listener for Show All Stickers button
        document.getElementById("show-all-stickers").addEventListener("click", () => {
            clearContainer();
            setMode("multiple");
            showAllStickers();
        });

        // Set up event listener for Show All Sticker Placements button
        document.getElementById("show-all-placements").addEventListener("click", () => {
            clearContainer();
            setMode("multiple");
            showAllStickerPlacements();
        });

        // Set up event listener for Show All User Image Placements button
        document.getElementById("show-all-user-image-placements").addEventListener("click", () => {
            clearContainer();
            setMode("multiple");
            showAllUserImagePlacements();
        });

        // Set up event listener for Wallet Surface Test Matrix button
        document.getElementById("show-wallet-test-matrix").addEventListener("click", () => {
            clearContainer();
            setMode("multiple");
            showWalletSurfaceTestMatrix();
        });

        // Set up event listener for Rerender button
        document.getElementById("rerender-button").addEventListener("click", () => {
            rerenderCurrentDevice();
        });

        // Set up event listener for Reload & Rerender button
        document.getElementById("reload-rerender-button").addEventListener("click", () => {
            reloadAndRerender();
        });

        // Set up event listener for Debug Backgrounds checkbox
        document.getElementById("debug-bgs-checkbox").addEventListener("change", (event) => {
            CYODesign.current.debugOptions.drawBgs = event.target.checked;
            saveSettings();
            rerenderCurrentDevice();
        });

        // Set up event listener for Disable Rotations checkbox
        document.getElementById("disable-rotations-checkbox").addEventListener("change", (event) => {
            CYODesign.current.debugOptions.disableRotations = event.target.checked;
            saveSettings();
            rerenderCurrentDevice();
        });

        // Set up event listener for Show Safe Area checkbox
        document.getElementById("show-safe-area-checkbox").addEventListener("change", (event) => {
            CYODesign.current.debugOptions.showSafeArea = event.target.checked;
            saveSettings();
            rerenderCurrentDevice();
        });

        // Set up event listener for Show Bleed Insets checkbox
        document.getElementById("show-bleed-insets-checkbox").addEventListener("change", (event) => {
            CYODesign.current.debugOptions.showBleedInsets = event.target.checked;
            saveSettings();
            rerenderCurrentDevice();
        });

        // Set up event listener for Hide Product checkbox
        document.getElementById("hide-product-checkbox").addEventListener("change", (event) => {
            CYODesign.current.debugOptions.hideProduct = event.target.checked;
            saveSettings();
            rerenderCurrentDevice();
        });

        // Set up event listener for Hide Mask checkbox
        document.getElementById("hide-mask-checkbox").addEventListener("change", (event) => {
            CYODesign.current.debugOptions.hideMask = event.target.checked;
            saveSettings();
            rerenderCurrentDevice();
        });

        // Set up event listener for Isolate Mask checkbox
        document.getElementById("isolate-mask-checkbox").addEventListener("change", (event) => {
            CYODesign.current.debugOptions.isolateMask = event.target.checked;
            saveSettings();
            rerenderCurrentDevice();
        });

        // Set up event listener for Hide Overlay checkbox
        document.getElementById("hide-overlay-checkbox").addEventListener("change", (event) => {
            CYODesign.current.debugOptions.hideOverlay = event.target.checked;
            saveSettings();
            rerenderCurrentDevice();
        });

        // Set up event listener for Hide Colored Overlay checkbox
        document.getElementById("hide-colored-overlay-checkbox").addEventListener("change", (event) => {
            CYODesign.current.debugOptions.hideColoredOverlay = event.target.checked;
            saveSettings();
            rerenderCurrentDevice();
        });

        // Set up event listener for Show Print Image checkbox
        document.getElementById("show-print-image-checkbox").addEventListener("change", (event) => {
            showPrintImage = event.target.checked;
            saveSettings();
            rerenderCurrentDevice();
        });

        // Add event listener for device dropdown changes
        document.getElementById("device-id").addEventListener("change", () => {
            clearContainer();
            // Save settings on change
            saveSettings();
            updateColoredOverlayCheckboxState();
            updateWalletTestButtonVisibility();
            showSingleDevice();
        });

        // Add event listener for sticker set dropdown changes
        document.getElementById("sticker-set-id").addEventListener("change", () => {
            clearContainer();

            // Just update the sticker set without repopulating dropdowns
            const stickerSetId = document.getElementById("sticker-set-id").value;

            if (stickerSetId === "") {
                // "None" option selected - clear sticker set
                CYODesign.current.stickerSet = null;
                // Save settings on change
                saveSettings();
                updateDisableRotationsCheckboxState();
                renderDeviceWithCurrentParameters();
            } else {
                const stickerSet = stickerSetWithID(stickerSetId);

                if (stickerSet) {
                    CYODesign.current.stickerSet = stickerSet;
                    // Save settings on change
                    saveSettings();
                    updateDisableRotationsCheckboxState();
                    renderDeviceWithCurrentParameters();
                } else {
                    console.error(`Sticker set with ID ${stickerSetId} not found`);
                }
            }
        });

        // Add event listener for sticker placement dropdown changes
        document.getElementById("placement-type").addEventListener("change", () => {
            clearContainer();

            // Update the sticker placement setting
            const stickerPlacementValue = document.getElementById("placement-type").value;
            CYODesign.current.stickerPlacement = stickerPlacementValue;

            console.log(`Changed sticker placement to: ${stickerPlacementValue}`);
            // Save settings on change
            saveSettings();
            renderDeviceWithCurrentParameters();
        });

        // Add event listener for user image dropdown changes
        document.getElementById("user-image").addEventListener("change", () => {
            clearContainer();

            // Save settings on change
            saveSettings();
            renderDeviceWithCurrentParameters();
        });

        // Add event listener for user image style dropdown changes
        document.getElementById("user-image-style").addEventListener("change", () => {
            clearContainer();

            // Update the user image style setting
            const userImageStyleValue = document.getElementById("user-image-style").value;
            CYODesign.current.subjectStyle = userImageStyleValue;

            console.log(`Changed user image style to: ${userImageStyleValue}`);
            // Save settings on change
            saveSettings();
            renderDeviceWithCurrentParameters();
        });

        // Add event listener for user image placement dropdown changes
        document.getElementById("user-image-placement").addEventListener("change", () => {
            clearContainer();

            // Update the user image placement setting
            const userImagePlacementValue = document.getElementById("user-image-placement").value;
            CYODesign.current.subjectPlacement = userImagePlacementValue;

            console.log(`Changed user image placement to: ${userImagePlacementValue}`);
            // Save settings on change
            saveSettings();
            renderDeviceWithCurrentParameters();
        });

        // Add event listener for bg image dropdown changes
        document.getElementById("bg-image").addEventListener("change", () => {
            clearContainer();

            // Save settings on change
            saveSettings();
            renderDeviceWithCurrentParameters();
        });

        // Set up environment dropdown
        const envSelect = document.getElementById("environment");
        envSelect.value = getCurrentServerEnv();

        envSelect.addEventListener("change", (event) => {
            const newEnv = event.target.value;
            const envName = newEnv.charAt(0).toUpperCase() + newEnv.slice(1);
            if (confirm(`Switch to ${envName} environment? This will reload the page.`)) {
                setServerEnv(newEnv);
            } else {
                // Revert selection
                envSelect.value = getCurrentServerEnv();
            }
        });

        // Set up parameter panel event listeners and rerender callback
        optionPanels.setRerenderCallback(() => {
            if (currentMode === "single") {
                rerenderCurrentDevice();
            }
        });
        optionPanels.setupParameterEventListeners();

        populateInitial();
    } catch (error) {
        console.error("Error initializing test:", error);
    }
}

async function populateDeviceDropdown() {
    await Promise.all([
        fetchDevices(),
        fetchColors()
    ]);
    const devices = getAllDevices();

    if (!devices || devices.length === 0) {
        console.error("No devices available");
        return;
    }

    // Group devices by productCategory with grips first
    const deviceGroups = {};
    devices.forEach(device => {
        const category = device.productCategory || 'other';
        if (!deviceGroups[category]) {
            deviceGroups[category] = [];
        }
        deviceGroups[category].push(device);
    });

    // Sort devices within each group by title
    Object.keys(deviceGroups).forEach(category => {
        deviceGroups[category].sort((a, b) => a.title.localeCompare(b.title));
    });

    // Define category order (grips first)
    const categoryOrder = ['grip', 'case', 'wallet'];
    const sortedCategories = categoryOrder.filter(cat => deviceGroups[cat]);
    // Add any other categories not in the predefined order
    Object.keys(deviceGroups).forEach(cat => {
        if (!categoryOrder.includes(cat)) {
            sortedCategories.push(cat);
        }
    });

    const deviceSelect = document.getElementById("device-id");
    const currentValue = deviceSelect.value; // Save current selection

    // Only populate if empty
    if (deviceSelect.options.length === 0) {
        // Add devices grouped by category
        sortedCategories.forEach(category => {
            // Add category header
            if (sortedCategories.length > 1) {
                const categoryHeader = document.createElement("optgroup");
                categoryHeader.label = category.charAt(0).toUpperCase() + category.slice(1) + 's';
                deviceSelect.appendChild(categoryHeader);

                // Add devices in this category
                deviceGroups[category].forEach(device => {
                    const option = document.createElement("option");
                    option.value = device.id;
                    option.text = `${device.title} (${device.id})`;
                    categoryHeader.appendChild(option);
                });
            } else {
                // If only one category, don't use optgroups
                deviceGroups[category].forEach(device => {
                    const option = document.createElement("option");
                    option.value = device.id;
                    option.text = `${device.title} (${device.id})`;
                    deviceSelect.appendChild(option);
                });
            }
        });
    }

    // Restore selection if it exists
    if (currentValue && deviceSelect.options.length > 0) {
        deviceSelect.value = currentValue;
        // If the value doesn't exist anymore, it will default to the first option
    }
}

async function populateStickerSetDropdown() {
    await fetchStickerSets(false, true);

    if (!allStickerSets || allStickerSets.length === 0) {
        console.error("No sticker sets available");
        return;
    }

    // Sort sticker sets by title
    const sortedStickerSets = [...allStickerSets].sort((a, b) => {
        // Handle cases where title might be missing
        const titleA = a.title || a.id;
        const titleB = b.title || b.id;
        return titleA.localeCompare(titleB);
    });

    const stickerSetSelect = document.getElementById("sticker-set-id");
    const currentValue = stickerSetSelect.value; // Save current selection

    // Only populate if empty
    if (stickerSetSelect.options.length === 0) {
        // Add "None" option first
        const noneOption = document.createElement("option");
        noneOption.value = "";
        noneOption.text = "None";
        stickerSetSelect.appendChild(noneOption);

        // Add sorted sticker sets to dropdown
        sortedStickerSets.forEach(stickerSet => {
            const option = document.createElement("option");
            option.value = stickerSet.id;

            // Use title if available, otherwise use ID
            const displayName = stickerSet.title || stickerSet.id;
            option.text = `${displayName} (${stickerSet.id})`;

            stickerSetSelect.appendChild(option);
        });
    }

    // Restore selection if it exists
    if (currentValue && stickerSetSelect.options.length > 0) {
        stickerSetSelect.value = currentValue;
        // If the value doesn't exist anymore, it will default to the first option
    }
}

async function populateInitial() {
    try {
        // Load saved settings
        const savedSettings = loadSettings();

        // Populate dropdowns first
        await populateStickerSetDropdown();
        await populateDeviceDropdown();

        // Set the debug checkboxes based on saved settings
        document.getElementById("debug-bgs-checkbox").checked = CYODesign.current.debugOptions.drawBgs;
        document.getElementById("disable-rotations-checkbox").checked = CYODesign.current.debugOptions.disableRotations;
        document.getElementById("show-safe-area-checkbox").checked = CYODesign.current.debugOptions.showSafeArea;
        document.getElementById("show-bleed-insets-checkbox").checked = CYODesign.current.debugOptions.showBleedInsets;
        document.getElementById("hide-product-checkbox").checked = CYODesign.current.debugOptions.hideProduct;
        document.getElementById("hide-mask-checkbox").checked = CYODesign.current.debugOptions.hideMask;
        document.getElementById("isolate-mask-checkbox").checked = CYODesign.current.debugOptions.isolateMask;
        document.getElementById("hide-overlay-checkbox").checked = CYODesign.current.debugOptions.hideOverlay;
        document.getElementById("hide-colored-overlay-checkbox").checked = CYODesign.current.debugOptions.hideColoredOverlay;
        document.getElementById("show-print-image-checkbox").checked = showPrintImage;

        // Apply saved settings
        if (savedSettings.stickerSetId) {
            const stickerSetSelect = document.getElementById("sticker-set-id");
            // Check if the saved sticker set exists in the dropdown
            const stickerSetExists = Array.from(stickerSetSelect.options).some(option => option.value === savedSettings.stickerSetId);

            if (stickerSetExists) {
                stickerSetSelect.value = savedSettings.stickerSetId;
            }
        }

        // Get the selected sticker set (either saved or default)
        const stickerSetId = document.getElementById("sticker-set-id").value;
        
        if (stickerSetId === "") {
            // "None" option selected - set sticker set to null
            CYODesign.current.stickerSet = null;
        } else {
            const stickerSet = stickerSetWithID(stickerSetId);

            if (!stickerSet) {
                console.error(`Sticker set with ID ${stickerSetId} not found`);
                return;
            }

            CYODesign.current.stickerSet = stickerSet;
        }

        // Apply saved device if it exists
        if (savedSettings.deviceId) {
            const deviceSelect = document.getElementById("device-id");
            // Check if the saved device exists in the dropdown
            const deviceExists = Array.from(deviceSelect.options).some(option => option.value === savedSettings.deviceId);

            if (deviceExists) {
                deviceSelect.value = savedSettings.deviceId;
            }
        }

        // Apply saved sticker placement if it exists
        if (savedSettings.stickerPlacementType) {
            const stickerPlacementSelect = document.getElementById("placement-type");
            // Check if the saved sticker placement exists in the dropdown
            const stickerPlacementExists = Array.from(stickerPlacementSelect.options).some(option => option.value === savedSettings.stickerPlacementType);

            if (stickerPlacementExists) {
                stickerPlacementSelect.value = savedSettings.stickerPlacementType;
                CYODesign.current.stickerPlacement = savedSettings.stickerPlacementType;
            }
        }


        // Apply saved user image placement if it exists
        if (savedSettings.userImagePlacement) {
            const userImagePlacementSelect = document.getElementById("user-image-placement");
            // Check if the saved user image placement exists in the dropdown
            const userImagePlacementExists = Array.from(userImagePlacementSelect.options).some(option => option.value === savedSettings.userImagePlacement);

            if (userImagePlacementExists) {
                userImagePlacementSelect.value = savedSettings.userImagePlacement;
                CYODesign.current.subjectPlacement = savedSettings.userImagePlacement;
            }
        }

        // Apply saved user image if it exists
        if (savedSettings.userImage !== null) {
            const userImageSelect = document.getElementById("user-image");
            // Check if the saved user image exists in the dropdown
            const userImageExists = Array.from(userImageSelect.options).some(option => option.value === savedSettings.userImage);

            if (userImageExists) {
                userImageSelect.value = savedSettings.userImage;
            }
        }

        // Apply saved user image style if it exists
        if (savedSettings.userImageStyle) {
            const userImageStyleSelect = document.getElementById("user-image-style");
            // Check if the saved user image style exists in the dropdown
            const userImageStyleExists = Array.from(userImageStyleSelect.options).some(option => option.value === savedSettings.userImageStyle);

            if (userImageStyleExists) {
                userImageStyleSelect.value = savedSettings.userImageStyle;
                CYODesign.current.subjectStyle = savedSettings.userImageStyle;
            }
        }

        // Apply saved bg image if it exists
        if (savedSettings.bgImage !== null) {
            const bgImageSelect = document.getElementById("bg-image");
            // Check if the saved bg image exists in the dropdown
            const bgImageExists = Array.from(bgImageSelect.options).some(option => option.value === savedSettings.bgImage);

            if (bgImageExists) {
                bgImageSelect.value = savedSettings.bgImage;
            }
        }

        // Update colored overlay checkbox state based on selected device
        updateColoredOverlayCheckboxState();

        // Update disable rotations checkbox state based on selected sticker set
        updateDisableRotationsCheckboxState();

        // Update wallet test button visibility based on selected device
        updateWalletTestButtonVisibility();

        // Show the selected device
        await showSingleDevice();

        console.log("Device test initialized with saved settings");
    } catch (error) {
        console.error("Error running test:", error);
    }
}

function clearContainer() {
    const container = document.querySelector(".container");
    container.innerHTML = "";
}

function updateColoredOverlayCheckboxState() {
    const deviceId = document.getElementById("device-id").value;
    const device = deviceWithID(deviceId);
    const checkbox = document.getElementById("hide-colored-overlay-checkbox");
    const label = document.querySelector('label[for="hide-colored-overlay-checkbox"]');

    if (!checkbox || !label) return;

    const hasColoredOverlays = device && device.coloredOverlays;

    if (hasColoredOverlays) {
        // Enable checkbox
        checkbox.disabled = false;
        checkbox.style.opacity = "1";
        label.style.opacity = "1";
        label.style.color = "#333";
    } else {
        // Disable and dim checkbox
        checkbox.disabled = true;
        checkbox.style.opacity = "0.5";
        label.style.opacity = "0.5";
        label.style.color = "#999";
        // Uncheck when disabled
        checkbox.checked = false;
        CYODesign.current.debugOptions.hideColoredOverlay = false;
        saveSettings();
    }
}

function updateDisableRotationsCheckboxState() {
    const stickerSetId = document.getElementById("sticker-set-id").value;
    const stickerSet = stickerSetId === "" ? null : stickerSetWithID(stickerSetId);
    const checkbox = document.getElementById("disable-rotations-checkbox");
    const label = document.querySelector('label[for="disable-rotations-checkbox"]');

    if (!checkbox || !label) return;

    const stickerSetDisablesRotations = stickerSet && stickerSet.disableRotations === true;

    if (stickerSetDisablesRotations) {
        // Force checkbox to be checked and disabled
        checkbox.checked = true;
        checkbox.disabled = true;
        checkbox.style.opacity = "0.5";
        label.style.opacity = "0.5";
        label.style.color = "#999";
        CYODesign.current.debugOptions.disableRotations = true;
        saveSettings();
    } else {
        // Enable checkbox for user control
        checkbox.disabled = false;
        checkbox.style.opacity = "1";
        label.style.opacity = "1";
        label.style.color = "#333";
    }
}

function updateWalletTestButtonVisibility() {
    const deviceId = document.getElementById("device-id").value;
    const device = deviceWithID(deviceId);
    const btn = document.getElementById("show-wallet-test-matrix");

    if (btn) {
        btn.style.display = (device?.productCategory === "wallet") ? "block" : "none";
    }
}

async function showSingleDevice() {
    const deviceId = document.getElementById("device-id").value;
    const device = deviceWithID(deviceId);

    if (!device) {
        console.error(`Device with ID ${deviceId} not found`);
        return;
    }

    // Set mode and store original device
    setMode("single");
    optionPanels.setOriginalDevice(device);
    
    // Reset parameter overrides for new device
    optionPanels.resetParameterOverrides();
    
    // Ensure UI is updated with new device parameters
    optionPanels.updateParameterUI();

    // Use device with parameter overrides
    const deviceWithOverrides = optionPanels.getDeviceWithOverrides(device);
    processDevice(deviceWithOverrides);
}

async function rerenderCurrentDevice() {
    clearContainer();
    renderDeviceWithCurrentParameters();
}

async function renderDeviceWithCurrentParameters() {
    const deviceId = document.getElementById("device-id").value;
    const device = deviceWithID(deviceId);

    if (!device) {
        console.error(`Device with ID ${deviceId} not found`);
        return;
    }

    // Set mode but DON'T reset parameter overrides
    setMode("single");
    
    // Update originalDevice if it changed
    if (optionPanels.getOriginalDevice() !== device) {
        optionPanels.setOriginalDevice(device);
    }
    
    // Always update parameter UI to refresh highlighting
    optionPanels.updateParameterUI();

    // Use device with parameter overrides
    const deviceWithOverrides = optionPanels.getDeviceWithOverrides(device);
    processDevice(deviceWithOverrides);
}

async function reloadAndRerender() {
    // Clear the container first
    clearContainer();

    // Fetch fresh device data and colors
    await Promise.all([
        fetchDevices(true),
        fetchColors(true),
        fetchStickerSets(true, true)
    ]);

    // Update the currently selected device and sticker set
    const deviceId = document.getElementById("device-id").value;
    const device = deviceWithID(deviceId);

    const stickerSetId = document.getElementById("sticker-set-id").value;
    const stickerSet = stickerSetWithID(stickerSetId);

    if (stickerSet) {
        CYODesign.current.stickerSet = stickerSet;
    }

    // Render with fresh data
    showSingleDevice();
}

// Helper function to preload images with concurrency limiting
async function preloadImagesWithLimit(imageTasks, progressText, progressLabel) {
    const CONCURRENCY_LIMIT = 6;
    const totalImages = imageTasks.length;
    let loadedCount = 0;

    for (let i = 0; i < imageTasks.length; i += CONCURRENCY_LIMIT) {
        const batch = imageTasks.slice(i, Math.min(i + CONCURRENCY_LIMIT, imageTasks.length));

        await Promise.all(batch.map(async task => {
            try {
                if (task.url) {
                    await imageCache.getLoadAndStoreURL(task.url);
                } else {
                    await imageCache.getLoadAndStoreCF(task.imageID, task.maxWidth, "webp", task.quality, task.dpr);
                }
                loadedCount++;
                if (progressText) {
                    progressText.textContent = `${progressLabel}: ${loadedCount}/${totalImages}`;
                }
            } catch (error) {
                const isDecodeError = error.name === "EncodingError" || error.message.includes("decode");
                if (isDecodeError) {
                    console.warn(`Decode error, may work on render`);
                } else {
                    console.warn(`Failed to load image:`, error.message);
                    if (task.onError) task.onError(task);
                }
                loadedCount++;
                if (progressText) {
                    progressText.textContent = `${progressLabel}: ${loadedCount}/${totalImages}`;
                }
            }
        }));
    }
}

// Helper function to render items in batches
async function renderInBatches(items, renderFunc, progressText, progressLabel, batchSize = 3) {
    let renderedCount = 0;

    for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, Math.min(i + batchSize, items.length));
        await Promise.all(batch.map(renderFunc));
        renderedCount += batch.length;
        if (progressText) {
            progressText.textContent = `${progressLabel}: ${renderedCount}/${items.length}`;
        }
        if (i + batchSize < items.length) {
            await new Promise(resolve => requestAnimationFrame(resolve));
        }
    }
}

async function showAllDevices() {
    const devices = getAllDevices();

    if (!devices || devices.length === 0) {
        console.error("No devices available");
        return;
    }

    console.log(`Setting up ${devices.length} devices`);

    const progressDiv = document.createElement("div");
    progressDiv.style.cssText = "position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 1000; text-align: center;";
    progressDiv.innerHTML = `<div style="font-weight: bold; margin-bottom: 10px;">Loading devices...</div><div id="progress-text">Preloading images...</div>`;
    document.body.appendChild(progressDiv);

    try {
        console.time("Preload all images");
        const dpr = Math.round(window.devicePixelRatio);
        const maxWidth = 400;
        const quality = 85;
        const progressText = document.getElementById("progress-text");

        // Build image tasks and track failures
        const deviceImageMap = new Map();
        const imageTasks = [];

        devices.forEach(device => {
            deviceImageMap.set(device.id, { device, failedImages: new Set() });

            const coloredOverlayImageID = device.coloredOverlays && device.colors && device.colors.length > 0
                ? getColoredOverlayImageID(device, { color: { id: device.colors[0], name: device.colors[0] } })
                : null;

            const addTask = (imageID, critical) => {
                imageTasks.push({
                    deviceId: device.id,
                    imageID,
                    maxWidth,
                    quality,
                    dpr,
                    onError: critical ? (task) => deviceImageMap.get(task.deviceId).failedImages.add(task.imageID) : null
                });
            };

            addTask(device.imageID, true);
            addTask(device.maskID, true);
            if (device.overlayID) addTask(device.overlayID, false);
            if (coloredOverlayImageID) addTask(coloredOverlayImageID, false);
        });

        await preloadImagesWithLimit(imageTasks, progressText, "Preloading images");

        const validDevices = devices.filter(d => deviceImageMap.get(d.id).failedImages.size === 0);
        const skippedCount = devices.length - validDevices.length;
        if (skippedCount > 0) {
            console.warn(`Skipping ${skippedCount} device(s) due to failed critical images`);
        }

        console.timeEnd("Preload all images");
        console.time("Render all devices");

        const skipSuffix = skippedCount > 0 ? ` (${skippedCount} skipped)` : '';
        await renderInBatches(validDevices, processDevice, progressText, `Rendering devices${skipSuffix}`);

        console.timeEnd("Render all devices");
    } finally {
        document.body.removeChild(progressDiv);
    }
}

async function showAllStickers() {
    const deviceId = document.getElementById("device-id").value;
    const device = deviceWithID(deviceId);

    if (!device) {
        console.error(`Device with ID ${deviceId} not found`);
        return;
    }

    await fetchStickerSets(false, true);

    if (!allStickerSets || allStickerSets.length === 0) {
        console.error("No sticker sets available");
        return;
    }

    console.log(`Setting up ${allStickerSets.length} sticker sets for device ${device.title}`);

    const header = document.createElement("h2");
    header.textContent = `All Sticker Sets for ${device.title}`;
    header.style.textAlign = "center";
    header.style.marginBottom = "20px";
    document.querySelector(".container").appendChild(header);

    const progressDiv = document.createElement("div");
    progressDiv.style.cssText = "position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 1000; text-align: center;";
    progressDiv.innerHTML = `<div style="font-weight: bold; margin-bottom: 10px;">Loading sticker sets...</div><div id="sticker-progress-text">Preloading images...</div>`;
    document.body.appendChild(progressDiv);

    try {
        console.time("Preload device images");
        const dpr = Math.round(window.devicePixelRatio);
        const maxWidth = 400;
        const quality = 85;
        const progressText = document.getElementById("sticker-progress-text");

        // Preload device images (once)
        const coloredOverlayImageID = device.coloredOverlays && device.colors && device.colors.length > 0
            ? getColoredOverlayImageID(device, { color: { id: device.colors[0], name: device.colors[0] } })
            : null;

        const deviceImageTasks = [
            { imageID: device.imageID, maxWidth, quality, dpr },
            { imageID: device.maskID, maxWidth, quality, dpr }
        ];

        if (device.overlayID) deviceImageTasks.push({ imageID: device.overlayID, maxWidth, quality, dpr });
        if (coloredOverlayImageID) deviceImageTasks.push({ imageID: coloredOverlayImageID, maxWidth, quality, dpr });

        await preloadImagesWithLimit(deviceImageTasks, progressText, "Preloading device images");
        console.timeEnd("Preload device images");

        // Preload all sticker images
        console.time("Preload all sticker images");
        const stickerTasks = allStickerSets.flatMap(stickerSet =>
            (stickerSet.stickerIDs || []).map(stickerID => ({
                url: `https://imagedelivery.net/${cfAccountHash}/${stickerID}/w=10000,format=webp,quality=85,dpr=1`
            }))
        );

        await preloadImagesWithLimit(stickerTasks, progressText, "Preloading sticker images");
        console.timeEnd("Preload all sticker images");

        // Render sticker sets
        console.time("Render all sticker sets");
        await renderInBatches(allStickerSets, async stickerSet => {
            const cyoDesignCopy = CYODesign.current.clone();
            cyoDesignCopy.stickerSet = stickerSet;

            const stickerTitle = document.createElement("div");
            stickerTitle.textContent = `${stickerSet.title || stickerSet.id}`;
            stickerTitle.style.cssText = "text-align: center; margin-bottom: 12px; font-weight: bold; font-size: 16px;";

            const wrapper = document.createElement("div");
            wrapper.style.cssText = "margin-bottom: 40px; display: flex; flex-direction: column; align-items: center;";
            wrapper.appendChild(stickerTitle);
            document.querySelector(".container").appendChild(wrapper);

            await renderDeviceWithOptions(device, cyoDesignCopy, wrapper);
        }, progressText, "Rendering sticker sets");

        console.timeEnd("Render all sticker sets");
    } finally {
        document.body.removeChild(progressDiv);
    }
}

async function showAllStickerPlacements() {
    // Get the currently selected device
    const deviceId = document.getElementById("device-id").value;
    const device = deviceWithID(deviceId);
    
    if (!device) {
        console.error(`Device with ID ${deviceId} not found`);
        return;
    }
    
    // Get the currently selected sticker set
    const stickerSetId = document.getElementById("sticker-set-id").value;
    const stickerSet = stickerSetWithID(stickerSetId);
    
    if (!stickerSet) {
        console.error(`Sticker set with ID ${stickerSetId} not found`);
        return;
    }
    
    // Get all sticker placement types
    const stickerPlacements = [
        { value: CYODesign.Placement.border, name: "Border" },
        { value: CYODesign.Placement.pattern, name: "Pattern" },
        { value: CYODesign.Placement.corner, name: "Corner" }
    ];
    
    console.log(`Setting up all sticker placements for device ${device.title} with sticker set ${stickerSet.title || stickerSet.id}`);
    
    // Create a header for this section
    const header = document.createElement("h2");
    header.textContent = `All Sticker Placement Types for ${device.title} with ${stickerSet.title || stickerSet.id}`;
    header.style.textAlign = "center";
    header.style.marginBottom = "20px";
    document.querySelector(".container").appendChild(header);
    
    // Process each sticker placement type
    for (const stickerPlacement of stickerPlacements) {
        // Create a copy of gallery options for this sticker placement
        const cyoDesignCopy = CYODesign.current.clone();
        cyoDesignCopy.stickerSet = stickerSet;
        cyoDesignCopy.stickerPlacement = stickerPlacement.value;
        
        // Create a title for this sticker placement
        const stickerPlacementTitle = document.createElement("div");
        stickerPlacementTitle.textContent = `Sticker Placement: ${stickerPlacement.name}`;
        stickerPlacementTitle.style.textAlign = "center";
        stickerPlacementTitle.style.marginBottom = "12px";
        stickerPlacementTitle.style.fontWeight = "bold";
        stickerPlacementTitle.style.fontSize = "16px";

        // Create a wrapper for this sticker placement
        const wrapper = document.createElement("div");
        wrapper.style.marginBottom = "40px";
        wrapper.style.display = "flex";
        wrapper.style.flexDirection = "column";
        wrapper.style.alignItems = "center";

        wrapper.appendChild(stickerPlacementTitle);
        document.querySelector(".container").appendChild(wrapper);

        // Render the device with this sticker placement using compositor
        await renderDeviceWithOptions(device, cyoDesignCopy, wrapper);
    }
}

async function showAllUserImagePlacements() {
    // Get the currently selected device
    const deviceId = document.getElementById("device-id").value;
    const device = deviceWithID(deviceId);
    
    if (!device) {
        console.error(`Device with ID ${deviceId} not found`);
        return;
    }
    
    // Get the currently selected user image
    const userImageValue = document.getElementById("user-image").value;
    
    if (!userImageValue) {
        console.error("No user image selected");
        return;
    }
    
    // Get all user image placement types
    const userImagePlacements = [
        { value: CYODesign.Placement.center, name: "Center" },
        { value: CYODesign.Placement.bottom, name: "Bottom" },
        { value: CYODesign.Placement.corner, name: "Corner" },
        { value: CYODesign.Placement.border, name: "Border" },
        { value: CYODesign.Placement.pattern, name: "Pattern" }
    ];
    
    console.log(`Setting up all user image placements for device ${device.title} with user image ${userImageValue}`);
    
    // Create a header for this section
    const header = document.createElement("h2");
    header.textContent = `All User Image Placement Types for ${device.title} with ${userImageValue}`;
    header.style.textAlign = "center";
    header.style.marginBottom = "20px";
    document.querySelector(".container").appendChild(header);
    
    // Process each user image placement type
    for (const userImagePlacement of userImagePlacements) {
        // Create a copy of gallery options for this user image placement
        const cyoDesignCopy = CYODesign.current.clone();
        cyoDesignCopy.subjectPlacement = userImagePlacement.value;
        
        // Create a title for this user image placement
        const userImagePlacementTitle = document.createElement("div");
        userImagePlacementTitle.textContent = `User Image Placement: ${userImagePlacement.name}`;
        userImagePlacementTitle.style.textAlign = "center";
        userImagePlacementTitle.style.marginBottom = "12px";
        userImagePlacementTitle.style.fontWeight = "bold";
        userImagePlacementTitle.style.fontSize = "16px";

        // Create a wrapper for this user image placement
        const wrapper = document.createElement("div");
        wrapper.style.marginBottom = "40px";
        wrapper.style.display = "flex";
        wrapper.style.flexDirection = "column";
        wrapper.style.alignItems = "center";

        wrapper.appendChild(userImagePlacementTitle);
        document.querySelector(".container").appendChild(wrapper);

        // Render the device with this user image placement using compositor
        await renderDeviceWithOptions(device, cyoDesignCopy, wrapper);
    }
}

// Helper function to load background image
async function loadBackgroundImage(bgImageValue) {
    if (!bgImageValue || bgImageValue === "") {
        return null;
    }

    try {
        const img = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                console.log(`Successfully loaded background image: ${bgImageValue}`);
                resolve(img);
            };
            img.onerror = (error) => {
                console.error(`Failed to load background image: ${bgImageValue}`, error);
                resolve(null);
            };

            if (bgImageValue === "fruit") {
                img.src = fruitImageUrl;
            } else if (bgImageValue === "blue-square") {
                img.src = blueSquareUrl;
            } else {
                resolve(null);
            }
        });

        return img;
    } catch (error) {
        console.error(`Error processing background image ${bgImageValue}:`, error);
        return null;
    }
}

// Helper function to load user image
async function loadUserImage(userImageValue) {
    if (!userImageValue || userImageValue === "") {
        return null;
    }

    try {
        let img = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                console.log(`Successfully loaded user image: ${userImageValue}`);
                resolve(img);
            };
            img.onerror = (error) => {
                console.error(`Failed to load user image: ${userImageValue}`, error);
                // Instead of rejecting, resolve with null to continue rendering
                resolve(null);
            };

            if (userImageValue === "dog") {
                img.src = dogStickerUrl;
            } else if (userImageValue === "joe") {
                img.src = joeImageUrl;
            } else if (userImageValue === "landscape") {
                img.src = landscapeImageUrl;
            } else if (userImageValue === "space-portrait") {
                img.src = spacePortraitImageUrl;
            } else if (userImageValue === "blue-square") {
                img.src = blueSquareUrl;
            } else if (userImageValue === "color-grid") {
                img.src = colorGridUrl;
            } else {
                resolve(null);
            }
        });

        if (!img) {
            return null;
        }

        // Check if we should remove background based on user image style setting
        const userImageStyle = document.getElementById("user-image-style").value;
        if (userImageStyle === CYODesign.Style.removeBackground) {
            console.log(`Removing background for image: ${userImageValue}`);
            
            // Convert image to file for background removal API
            const file = await convertImageToFile(img);
            
            // Remove background using the API
            const result = await cfProcessImage(file);
            
            if (result) {
                const { src: removedSrc, hasPeopleOrPets } = result;
                console.log(`People/pets detected in ${userImageValue}: ${hasPeopleOrPets}`);
                // Load the background-removed image
                const transparentImg = await loadImage(removedSrc);
                console.log(`Successfully removed background for: ${userImageValue}`);
                return transparentImg;
            } else {
                console.warn(`Background removal failed for: ${userImageValue}, using original image`);
                return img;
            }
        }

        return img;
    } catch (error) {
        console.error(`Error processing user image ${userImageValue}:`, error);
        return null;
    }
}

// Helper function that uses Compositor
async function renderDeviceWithOptions(device, cyoDesign, container) {
    try {
        // Prepare gallery options
        cyoDesign.stickerImages = await stickerArrayForCYODesign(cyoDesign);
        cyoDesign.debugOptions.drawBgs = CYODesign.current.debugOptions.drawBgs;

        // Load background image if selected
        const bgImageValue = document.getElementById("bg-image").value;
        const bgImage = await loadBackgroundImage(bgImageValue);
        cyoDesign.backgroundImage = bgImage;

        // Load user image if selected
        const userImageValue = document.getElementById("user-image").value;
        const userImage = await loadUserImage(userImageValue);

        if (userImage) {
            cyoDesign.transparentImage = userImage;
            cyoDesign.userImage = userImage;
        } else {
            cyoDesign.transparentImage = null;
            cyoDesign.userImage = null;
        }
        
        // Check if device has coloredOverlays and get colored overlay ID if present
        let coloredOverlayImageID = null;
        if (device.coloredOverlays && device.colors && device.colors.length > 0) {
            // Get the first color from the device's colors array
            const firstColorId = device.colors[0];
            const colorData = {
                color: { id: firstColorId, name: firstColorId }
            };
            
            // Get colored overlay image ID
            coloredOverlayImageID = getColoredOverlayImageID(device, colorData);
        }
        
        const compositor = await Compositor.createInContainer(container, device, cyoDesign, null, device.imageID, coloredOverlayImageID);
        compositor.updateCompositeView(null);

        // Generate and display print canvas if enabled
        if (showPrintImage && currentMode === "single") {
            await displayPrintCanvas(device, cyoDesign, container);
        }

        return compositor;
    } catch (error) {
        console.error("Error in renderDeviceWithOptions:", error);
        // Continue with null images if there's an error
        cyoDesign.transparentImage = null;
        cyoDesign.userImage = null;
        cyoDesign.backgroundImage = null;

        // Check if device has coloredOverlays and get colored overlay ID if present (error fallback)
        let coloredOverlayImageID = null;
        if (device.coloredOverlays && device.colors && device.colors.length > 0) {
            try {
                const firstColorId = device.colors[0];
                const colorData = {
                    color: { id: firstColorId, name: firstColorId }
                };

                coloredOverlayImageID = getColoredOverlayImageID(device, colorData);
            } catch (overlayError) {
                console.error("Error getting colored overlay in fallback:", overlayError);
            }
        }

        const compositor = await Compositor.createInContainer(container, device, cyoDesign, null, device.imageID, coloredOverlayImageID);
        compositor.updateCompositeView(null);

        // Generate and display print canvas if enabled
        if (showPrintImage && currentMode === "single") {
            await displayPrintCanvas(device, cyoDesign, container);
        }

        return compositor;
    }
}

// Display print canvas next to the normal canvas
async function displayPrintCanvas(device, cyoDesign, container) {
    try {
        console.log("Generating print-ready canvas...");
        
        // Set Product.device for CartUtils to use
        Product.device = device;

        // Set user images in ImageStorage for CartUtils to access (null clears them)
        imageStorage.setUploadedImage(cyoDesign.userImage);
        imageStorage.setTransparentImage(cyoDesign.transparentImage);
        
        // Generate print canvas using CartUtils
        const printCanvas = await CartUtils.generatePrintReadyCanvas(cyoDesign);
        
        // Create a wrapper for the print canvas
        const printWrapper = document.createElement("div");
        printWrapper.style.display = "flex";
        printWrapper.style.flexDirection = "column";
        printWrapper.style.alignItems = "center";
        printWrapper.style.marginLeft = "20px";
        
        // Style the print canvas to match preview canvas size
        printCanvas.style.width = "100%";
        printCanvas.style.height = "100%";
        printCanvas.style.objectFit = "contain";
        printCanvas.style.display = "block";
        printCanvas.style.maxWidth = "400px";
        printCanvas.style.backgroundColor = "transparent";
        printCanvas.style.border = "1px solid #ccc";

        // Create size label for print canvas
        const printSizeLabel = document.createElement("div");
        printSizeLabel.textContent = `${printCanvas.width} × ${printCanvas.height}`;
        printSizeLabel.style.textAlign = "center";
        printSizeLabel.style.marginTop = "5px";
        printSizeLabel.style.fontSize = "12px";
        printSizeLabel.style.color = "#888";
        printSizeLabel.style.fontFamily = "monospace";

        // Create button for recipe assets
        const recipeAssetsButton = document.createElement("button");
        recipeAssetsButton.textContent = "Get Recipe Assets";
        recipeAssetsButton.className = "mode-button";
        recipeAssetsButton.style.marginTop = "10px";
        recipeAssetsButton.style.padding = "8px 16px";
        recipeAssetsButton.style.fontSize = "13px";
        recipeAssetsButton.addEventListener("click", async () => {
            await fetchAndDisplayRecipeAssets(printCanvas, device);
        });

        // Add print canvas, size label, and button to wrapper
        printWrapper.appendChild(printCanvas);
        printWrapper.appendChild(printSizeLabel);
        printWrapper.appendChild(recipeAssetsButton);
        
        // Create a horizontal layout wrapper for both canvases
        const horizontalWrapper = document.createElement("div");
        horizontalWrapper.style.display = "flex";
        horizontalWrapper.style.flexDirection = "row";
        horizontalWrapper.style.alignItems = "flex-start";
        horizontalWrapper.style.justifyContent = "center";
        horizontalWrapper.style.gap = "20px";
        horizontalWrapper.style.width = "100%";

        // Find the existing compositor container and move it into horizontal wrapper
        const existingCompositeContainer = container.querySelector(".composite-container");
        if (existingCompositeContainer) {
            // Create wrapper for preview canvas with size label
            const previewWrapper = document.createElement("div");
            previewWrapper.style.display = "flex";
            previewWrapper.style.flexDirection = "column";
            previewWrapper.style.alignItems = "center";

            // Get preview canvas dimensions from the compositor display canvas
            const previewCanvas = existingCompositeContainer.querySelector(".composite-canvas");
            const previewSizeLabel = document.createElement("div");
            if (previewCanvas) {
                previewSizeLabel.textContent = `${previewCanvas.width} × ${previewCanvas.height}`;
            } else {
                previewSizeLabel.textContent = "Unknown size";
            }
            previewSizeLabel.style.textAlign = "center";
            previewSizeLabel.style.marginTop = "5px";
            previewSizeLabel.style.fontSize = "12px";
            previewSizeLabel.style.color = "#888";
            previewSizeLabel.style.fontFamily = "monospace";

            // Add preview canvas and its size label to wrapper
            previewWrapper.appendChild(existingCompositeContainer);
            previewWrapper.appendChild(previewSizeLabel);

            // Move both wrappers into the horizontal wrapper
            horizontalWrapper.appendChild(previewWrapper);
            horizontalWrapper.appendChild(printWrapper);
            
            // Add the horizontal wrapper to the main container
            container.appendChild(horizontalWrapper);
        } else {
            // Fallback: just add both to container with flex
            container.style.display = "flex";
            container.style.flexDirection = "row";
            container.style.alignItems = "flex-start";
            container.style.justifyContent = "center";
            container.style.gap = "20px";
            container.appendChild(printWrapper);
        }
        
        console.log("Print-ready canvas generated and displayed");
    } catch (error) {
        console.error("Error generating print canvas:", error);
        
        // Show error message next to normal canvas
        const errorWrapper = document.createElement("div");
        errorWrapper.style.display = "flex";
        errorWrapper.style.flexDirection = "column";
        errorWrapper.style.alignItems = "center";
        errorWrapper.style.marginLeft = "20px";
        errorWrapper.style.color = "#d32f2f";
        
        const errorTitle = document.createElement("div");
        errorTitle.textContent = "Print Canvas Error";
        errorTitle.style.textAlign = "center";
        errorTitle.style.marginBottom = "10px";
        errorTitle.style.fontWeight = "bold";
        errorTitle.style.fontSize = "14px";
        
        const errorMessage = document.createElement("div");
        errorMessage.textContent = error.message || "Failed to generate print canvas";
        errorMessage.style.textAlign = "center";
        errorMessage.style.fontSize = "12px";
        errorMessage.style.maxWidth = "200px";
        errorMessage.style.wordWrap = "break-word";
        
        errorWrapper.appendChild(errorTitle);
        errorWrapper.appendChild(errorMessage);
        
        // Create a horizontal layout wrapper for error display
        const horizontalWrapper = document.createElement("div");
        horizontalWrapper.style.display = "flex";
        horizontalWrapper.style.flexDirection = "row";
        horizontalWrapper.style.alignItems = "flex-start";
        horizontalWrapper.style.justifyContent = "center";
        horizontalWrapper.style.gap = "20px";
        horizontalWrapper.style.width = "100%";

        // Find the existing compositor container and move it into horizontal wrapper
        const existingCompositeContainer = container.querySelector(".composite-container");
        if (existingCompositeContainer) {
            // Move the existing canvas into the horizontal wrapper
            horizontalWrapper.appendChild(existingCompositeContainer);
            horizontalWrapper.appendChild(errorWrapper);
            
            // Add the horizontal wrapper to the main container
            container.appendChild(horizontalWrapper);
        } else {
            // Fallback: just add error to container with flex
            container.style.display = "flex";
            container.style.flexDirection = "row";
            container.style.alignItems = "flex-start";
            container.style.justifyContent = "center";
            container.style.gap = "20px";
            container.appendChild(errorWrapper);
        }
    }
}

// Process each device with a delay
async function processDevice(device) {
    const cyoDesignCopy = CYODesign.current.clone();

    // Create a label with device ID and title
    const deviceLabel = document.createElement("div");
    deviceLabel.textContent = `${device.id} - ${device.title}`;
    deviceLabel.style.textAlign = "center";
    deviceLabel.style.marginBottom = "12px";

    // Create a wrapper container for both label and canvas
    const deviceWrapper = document.createElement("div");
    deviceWrapper.style.display = "flex";
    deviceWrapper.style.flexDirection = "column";
    deviceWrapper.style.alignItems = "center";
    deviceWrapper.style.marginBottom = "30px";

    // Add label to the wrapper
    deviceWrapper.appendChild(deviceLabel);
    
    // Render device using compositor
    await renderDeviceWithOptions(device, cyoDesignCopy, deviceWrapper);

    // Add the wrapper to the main container
    const container = document.querySelector(".container");
    container.appendChild(deviceWrapper);
}

async function stickerArrayForCYODesign(cyoDesign) {
    const stickerSet = cyoDesign.stickerSet;
    if (stickerSet == null) {
        return [];
    }
    return await stickerSetFeed.imageArrayForSetWithID(stickerSet.id);
}

// Fetch and display recipe assets using consolidated CartUtils method
async function fetchAndDisplayRecipeAssets(printCanvas, device) {
    try {
        // Create recipe (color is always null in sticker-test)
        const recipe = await CartUtils.createRecipe(printCanvas, device, null, null);

        // Use consolidated CartUtils method to fetch and display assets
        await CartUtils.fetchAndDisplayRecipeAssets(recipe);
    } catch (error) {
        console.error("Error fetching recipe assets:", error);
        alert(`Failed to fetch recipe assets: ${error.message}`);
    }
}

// Wallet Surface Test Matrix - renders all combinations of surface modes, user placements, and sticker placements
async function showWalletSurfaceTestMatrix() {
    const deviceId = document.getElementById("device-id").value;
    const device = deviceWithID(deviceId);

    if (!device || device.productCategory !== "wallet") {
        console.error("Wallet device required for surface test matrix");
        return;
    }

    // Find "squares (debug)" sticker set dynamically
    const stickerSet = allStickerSets.find(s =>
        (s.title?.toLowerCase().includes("squares")) ||
        s.id.toLowerCase().includes("squares")
    );
    if (!stickerSet) {
        console.error("Could not find squares debug sticker set");
        return;
    }

    // Define test matrix
    const surfaceModes = ["wallet", "both", "split", "grip"];
    const stickerPlacements = {
        wallet: ["full", "border", "pattern", "corner"],
        both: ["full", "border", "pattern", "corner"],
        split: ["full", "border", "pattern", "corner"],
        grip: ["full", "border", "pattern"]
    };
    const userPlacements = {
        wallet: ["center", "bottom", "border", "pattern", "corner"],
        both: ["center", "bottom", "border", "pattern", "corner"],
        split: ["center", "bottom", "border", "pattern"],  // no corner for split (user image renders on grip)
        grip: ["center", "bottom", "border", "pattern"]  // no corner for grip
    };

    const container = document.querySelector(".container");
    container.innerHTML = "";

    // Create progress indicator
    // Calculate total cells accounting for border/pattern/corner sync rule
    // "full" sticker placement is always available; split mode allows all combinations
    const totalCells = surfaceModes.reduce((sum, mode) => {
        let modeTotal = 0;
        for (const userPlacement of userPlacements[mode]) {
            if (mode === "split") {
                // Split mode allows all combinations
                modeTotal += stickerPlacements[mode].length;
            } else if (userPlacement === "border" || userPlacement === "pattern" || userPlacement === "corner") {
                // Matching sticker placement + "full"
                modeTotal += 2;
            } else {
                // All sticker placements available
                modeTotal += stickerPlacements[mode].length;
            }
        }
        return sum + modeTotal;
    }, 0);
    let renderedCount = 0;

    const progressDiv = document.createElement("div");
    progressDiv.style.cssText = "position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 1000; text-align: center;";
    progressDiv.innerHTML = `<div style="font-weight: bold; margin-bottom: 10px;">Rendering test matrix...</div><div id="matrix-progress-text">Preloading assets...</div>`;
    document.body.appendChild(progressDiv);

    const progressText = document.getElementById("matrix-progress-text");

    try {
        // PRE-LOAD ASSETS ONCE (major performance optimization)
        console.time("Matrix preload");

        // 1. Load user image from dropdown selection ONCE
        const userImageValue = document.getElementById("user-image").value;
        progressText.textContent = "Loading user image...";
        const userImage = userImageValue ? await loadUserImage(userImageValue) : null;

        // 2. Load sticker images ONCE
        progressText.textContent = "Loading sticker images...";
        const stickerImages = await stickerSetFeed.imageArrayForSetWithID(stickerSet.id);

        // 2b. Load gingham image for "full" sticker placement
        progressText.textContent = "Loading gingham image...";
        const ginghamImage = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = (e) => {
                console.error("Failed to load gingham.png:", e);
                reject(e);
            };
            img.src = ginghamImageUrl;
        }).catch(e => {
            console.warn("Gingham image not found, skipping 'full' sticker placement");
            return null;
        });

        // 3. Get grip device for split/grip modes
        const gripDevice = deviceWithID("C3PrebuiltPopTopOnly");

        // 4. Get colored overlay ID - coloredOverlays uses altColor (grip color) as key
        let coloredOverlayImageID = null;
        if (device.coloredOverlays) {
            // Get first key from coloredOverlays to use as the altColor
            const firstAltColorId = Object.keys(device.coloredOverlays)[0];
            if (firstAltColorId) {
                // Pass as third parameter (altColor) - function signature is (device, primaryColor, altColor)
                coloredOverlayImageID = getColoredOverlayImageID(device, null, { id: firstAltColorId });
                console.log(`[Matrix] coloredOverlayImageID resolved: ${coloredOverlayImageID} for altColor: ${firstAltColorId}`);
            }
        }
        if (!coloredOverlayImageID) {
            console.warn("[Matrix] No coloredOverlayImageID - grip overlay won't render");
        }

        console.timeEnd("Matrix preload");
        console.time("Matrix render");

        // Build all render tasks grouped by surface mode
        for (const surfaceMode of surfaceModes) {
            // Create section wrapper to ensure new row
            const section = document.createElement("div");
            section.style.width = "100%";
            container.appendChild(section);

            // Create section header
            const header = document.createElement("h2");
            header.textContent = `${surfaceMode.toUpperCase()} MODE`;
            header.style.textAlign = "center";
            header.style.marginTop = "30px";
            header.style.marginBottom = "10px";
            header.style.borderBottom = "2px solid #333";
            header.style.paddingBottom = "10px";
            section.appendChild(header);

            // Iterate over user placements to create subsections
            for (const userPlacement of userPlacements[surfaceMode]) {
                // Create subsection header
                const subheader = document.createElement("h3");
                subheader.textContent = `User: ${userPlacement}`;
                subheader.style.textAlign = "center";
                subheader.style.marginTop = "15px";
                subheader.style.marginBottom = "10px";
                subheader.style.color = "#666";
                subheader.style.fontWeight = "normal";
                section.appendChild(subheader);

                // Create grid container for this subsection
                const grid = document.createElement("div");
                grid.style.display = "flex";
                grid.style.flexWrap = "nowrap";
                grid.style.gap = "20px";
                grid.style.justifyContent = "center";
                grid.style.marginBottom = "20px";
                section.appendChild(grid);

                // Build tasks for this user placement
                // Filter: border/pattern/corner user placements must match sticker placements (they're synced in the UI)
                // Exceptions: "full" sticker placement is always allowed; split mode allows all combinations
                const tasks = stickerPlacements[surfaceMode]
                    .filter(stickerPlacement => {
                        if (stickerPlacement === "full") return true; // "full" always allowed
                        if (surfaceMode === "split") return true; // All combinations allowed in split mode
                        if (userPlacement === "border") return stickerPlacement === "border";
                        if (userPlacement === "pattern") return stickerPlacement === "pattern";
                        if (userPlacement === "corner") return stickerPlacement === "corner";
                        return true;
                    })
                    .map(stickerPlacement => ({ stickerPlacement }));

                // Render in batches of 4 for parallelism
                const BATCH_SIZE = 4;
                for (let i = 0; i < tasks.length; i += BATCH_SIZE) {
                    const batch = tasks.slice(i, i + BATCH_SIZE);

                    await Promise.all(batch.map(async ({ stickerPlacement }) => {
                        // Create cell wrapper with label
                        const cell = document.createElement("div");
                        cell.style.textAlign = "center";

                        const label = document.createElement("div");
                        label.textContent = `Stickers: ${stickerPlacement}`;
                        label.style.fontSize = "10px";
                        label.style.marginBottom = "8px";
                        cell.appendChild(label);

                        // Create CYODesign with specific settings
                        const cyoDesign = CYODesign.current.clone();
                        cyoDesign.walletPrintSurface = surfaceMode;
                        cyoDesign.subjectPlacement = userPlacement;
                        // Use "removeBackground" style to match main app behavior
                        // This ensures correct z-order: user image on top of stickers for center/bottom placements
                        cyoDesign.subjectStyle = CYODesign.Style.removeBackground;

                        // Handle "full" sticker placement (uses gingham as background image, no stickers)
                        if (stickerPlacement === "full" && ginghamImage) {
                            cyoDesign.stickerSet = null;
                            cyoDesign.stickerPlacement = "border";  // doesn't matter, no stickers
                            cyoDesign.stickerImages = [];
                            cyoDesign.backgroundImage = ginghamImage;
                        } else if (stickerPlacement === "full") {
                            // Gingham image failed to load, skip this cell
                            return;
                        } else {
                            cyoDesign.stickerSet = stickerSet;
                            cyoDesign.stickerPlacement = stickerPlacement;
                            cyoDesign.stickerImages = stickerImages;
                        }
                        // Reset debug options that might hide overlays
                        cyoDesign.debugOptions.drawBgs = CYODesign.current.debugOptions.drawBgs;
                        cyoDesign.debugOptions.hideOverlay = false;
                        cyoDesign.debugOptions.hideColoredOverlay = false;

                        // Use the selected user image (if any)
                        if (userImage) {
                            cyoDesign.userImage = userImage;
                            cyoDesign.transparentImage = userImage;
                            // Note: Don't set backgroundImage = userImage for center placement
                            // With subjectStyle = removeBackground, the user image renders as a subject
                            // Setting backgroundImage would cause it to render twice
                        }

                        // Render into cell with pre-loaded assets
                        await renderMatrixCell(device, cyoDesign, cell, coloredOverlayImageID, gripDevice);

                        grid.appendChild(cell);
                    }));

                    renderedCount += batch.length;
                    progressText.textContent = `${renderedCount}/${totalCells}`;
                }
            }
        }

        console.timeEnd("Matrix render");
    } finally {
        document.body.removeChild(progressDiv);
    }
}

// Optimized cell renderer - uses pre-loaded assets
async function renderMatrixCell(device, cyoDesign, container, coloredOverlayImageID, gripDevice) {
    try {
        // Create main compositor
        const compositor = await Compositor.createInContainer(
            container, device, cyoDesign, null, device.imageID, coloredOverlayImageID
        );

        // For split/grip modes or independent both mode, create altCompositor with grip device
        const surfaceMode = cyoDesign.walletPrintSurface;
        let altCompositor = null;
        const needsAltCompositor = (surfaceMode === "split" || surfaceMode === "grip" ||
                                    cyoDesign.requiresIndependentSurfaceRendering?.(device));

        if (needsAltCompositor && gripDevice) {
            const tempContainer = document.createElement("div");
            altCompositor = await Compositor.createInContainer(
                tempContainer, gripDevice, cyoDesign, null, gripDevice.imageID, null
            );
            altCompositor.updateCompositeView(null);
        }

        compositor.updateCompositeView(null, altCompositor);

        // Scale down the canvas for grid display
        const compositeContainer = container.querySelector(".composite-container");
        if (compositeContainer) {
            compositeContainer.style.maxWidth = "200px";
        }

        return compositor;
    } catch (error) {
        console.error("Error in renderMatrixCell:", error);
        return null;
    }
}

// Make functions available globally for onclick handlers
window.resetParameter = (paramName, index) => optionPanels.resetParameter(paramName, index);
window.resetAllParameters = () => optionPanels.resetAllParameters();

// Initialize the test when the page loads
window.addEventListener("DOMContentLoaded", initializeTest);